import { ChevronDown, ChevronUp } from 'lucide-react';
import { type MouseEvent, useMemo, useState } from 'react';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { Stage } from '@/components/Stage/Stage';
import Typo from '@/components/Typography/Typo';
import FeedbackBlockTooltip from '@/features/Feedback/components/FeedbackBlockTooltip/FeedbackBlockTooltip';
import useFeedbackBlockTooltip from '@/features/Feedback/components/FeedbackBlockTooltip/useFeedbackBlockTooltip';
import { ContentStyle } from '@/styles/ContentStyle';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import feedbackDetailUtils from '../../utils/feedbackDetailutils';

type Props = {
  detail: FeedbackT;
  focusedBlock?: string;
};

export const DiscussionDisplay = (props: Props) => {
  const { tooltipInfo, currentBlockInfo, onMouseMove, onMouseLeave } =
    useFeedbackBlockTooltip({
      blocks: props.detail.blocks || [],
    });

  const textHTML = useMemo(() => {
    return feedbackDetailUtils
      .setBlockTags({
        text: props.detail.discussion?.body || '',
        blocks: props.detail.blocks || [],
        focusedBlock: props.focusedBlock,
      })
      .replaceAll('\n', '<br/>');
  }, [props.detail.blocks, props.detail.discussion?.body, props.focusedBlock]);

  return (
    <div>
      <ContentStyle
        dangerouslySetInnerHTML={{ __html: textHTML }}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
      />
      <FeedbackBlockTooltip
        tooltipInfo={tooltipInfo}
        currentBlockInfo={currentBlockInfo}
      />
      <CommnentDisplay
        comments={props.detail.discussion?.comments || []}
        detail={props.detail}
        focusedBlock={props.focusedBlock}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
      />
    </div>
  );
};

const CommnentDisplay = (props: {
  comments: CommentT[];
  detail: FeedbackT;
  focusedBlock?: string;
  onMouseLeave: () => void;
  onMouseMove: (e: MouseEvent) => void;
}) => {
  return (
    <div className="pt-6">
      <Typo type="label3" weight="sb">
        Comments ({props.comments.length})
      </Typo>
      <Space h="6px" />
      <div className="flex flex-col gap-1">
        {props.comments.map((comment) => {
          return (
            <CommentItem
              comment={comment}
              key={comment.id}
              detail={props.detail}
              focusedBlock={props.focusedBlock}
              onMouseLeave={props.onMouseLeave}
              onMouseMove={props.onMouseMove}
            />
          );
        })}
      </div>
    </div>
  );
};

const CommentItem = (props: {
  comment: CommentT;
  detail: FeedbackT;
  focusedBlock?: string;
  onMouseLeave: () => void;
  onMouseMove: (e: MouseEvent) => void;
}) => {
  const [childrenOpen, setChildrenOpen] = useState(false);
  const textHTML = useMemo(() => {
    return feedbackDetailUtils
      .setBlockTags({
        text: props.comment.text || '',
        blocks: props.detail.blocks || [],
        source_id: props.comment.id,
        focusedBlock: props.focusedBlock,
      })
      .replaceAll('\n', '<br/>');
  }, [
    props.comment.text,
    props.comment.id,
    props.detail.blocks,
    props.focusedBlock,
  ]);

  const childrenComment = props.detail.discussion?.comments?.filter(
    (comment) => comment.parent_id === props.comment.id,
  );

  return (
    <Flex>
      {props.comment.author_profile_url && (
        <img
          src={props.comment.author_profile_url || ''}
          width="38px"
          height="38px"
          alt={props.comment.author_name}
          style={{
            alignSelf: 'center',
            flexShrink: 0,
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
      )}
      <div className="flex items-start gap-2 p-3 bg-gray-150 transition-all duration-200 rounded-md">
        <div className="flex-1 flex-col relative">
          <div className="flex items-center gap-1.5">
            <Typo type="label3" weight="m">
              {props.comment.author_name}
            </Typo>
            <Typo type="body5" className="text-gray-600">
              {getTimezoneDayjs(props.comment.updated_at).format(
                DATE_FORMAT.readableHour,
              )}
            </Typo>
          </div>
          <Space w="2px" />
          <div className="text-gray-700">
            <ContentStyle
              dangerouslySetInnerHTML={{
                __html: textHTML,
              }}
              onMouseLeave={props.onMouseLeave}
              onMouseMove={props.onMouseMove}
            />
          </div>
          {props.comment.child_count > 0 && (
            <>
              <Space h={8} />
              <Stage>
                <Stage.Scene status={childrenOpen}>
                  <VFlex>
                    {childrenComment?.map((comment) => {
                      return (
                        <CommentItem
                          {...props}
                          comment={comment}
                          key={comment.id}
                        />
                      );
                    })}
                    <Button
                      leftIcon={<ChevronUp size={14} />}
                      sizeType="s"
                      variant="ghost"
                      onClick={() => setChildrenOpen(false)}
                      className="w-fit"
                    >
                      Hide {childrenComment?.length} replies
                    </Button>
                  </VFlex>
                </Stage.Scene>
                <Stage.Scene default status={!childrenOpen}>
                  <Button
                    leftIcon={<ChevronDown size={14} />}
                    sizeType="s"
                    variant="ghost"
                    onClick={() => setChildrenOpen(true)}
                    className="w-fit"
                  >
                    Show {childrenComment?.length} replies
                  </Button>
                </Stage.Scene>
              </Stage>
            </>
          )}
        </div>
      </div>
    </Flex>
  );
};
